import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";

import { ForwardIcon, MasterCard, Visa } from "../../assets/DirectTopUpIcon";
import {
  purchaseAirtime,
  resetPurchaseState as resetAirtimeState,
  selectPurchaseLoading as selectAirtimeLoading,
  selectPurchasePaymentUrl as selectAirtimePaymentUrl,
  selectPurchaseError as selectAirtimeError,
} from "../../features/store/airtimePurchaseSlice";
import {
  purchaseData,
  resetPurchaseState as resetDataState,
  selectPurchaseLoading as selectDataLoading,
  selectPurchasePaymentUrl as selectDataPaymentUrl,
  selectPurchaseError as selectDataError,
} from "../../features/store/dataPurchaseSlice";

import {
  purchaseElectricity,
  resetPurchaseState as resetElectricState,
  selectPurchaseLoading as selectElectricLoading,
  selectPurchasePaymentUrl as selectElectricPaymentUrl,
  selectPurchaseError as selectElectricError,
} from "../../features/store/electricityPurchaseSlice";
import {
  purchaseCable,
  resetPurchaseState as resetCableState,
  selectPurchaseLoading as selectCableLoading,
  selectPurchasePaymentUrl as selectCablePaymentUrl,
  selectPurchaseError as selectCableError,
} from "../../features/store/cablePurchaseSlice";
import {
  purchaseGiftcard,
  resetPurchaseState as resetGiftcardState,
  selectPurchaseLoading as selectGiftcardLoading,
  selectPurchasePaymentUrl as selectGiftcardPaymentUrl,
  selectPurchaseError as selectGiftcardError,
} from "../../features/store/giftcardPurchaseSlice";
import {
  purchaseTopup,
  resetPurchaseState as resetTopupState,
  selectPurchaseLoading as selectTopupLoading,
  selectPurchasePaymentUrl as selectTopupPaymentUrl,
  selectPurchaseError as selectTopupError,
} from "../../features/store/topupPurchaseSlice";
import {
  purchaseGamecard,
  resetPurchaseState as resetGamecardState,
  selectPurchaseLoading as selectGamecardLoading,
  selectPurchasePaymentUrl as selectGamecardPaymentUrl,
  selectPurchaseError as selectGamecardError,
} from "../../features/store/gamecardPurchaseSlice";
import {
  purchaseDGCoin,
  resetPurchaseState as resetDGCoinState,
  selectPurchaseLoading as selectDGCoinLoading,
  selectPurchasePaymentUrl as selectDGCoinPaymentUrl,
  selectPurchaseError as selectDGCoinError,
} from "../../features/store/dgcoinPurchaseSlice";
import { AppDispatch } from "../../redux/store";
import { useAppSelector } from "../../hooks/hooks";
import { TransactionData } from "../types";
import { AirtimePurchaseRequest } from "../../features/store/types/airtime-types";
import { DataPurchaseRequest } from "../../features/store/types/data-purchase-types";
import { CablePurchaseRequest } from "../../features/store/types/cable-purchase-types";
import { GiftcardPurchaseRequest } from "../../features/store/types/giftcard-purchase-types";
import { TopupPurchaseRequest } from "../../features/store/types/topup-purchase-types";
import { GamecardPurchaseRequest } from "../../features/store/types/gamecard-purchase-types";
import { DGCoinPurchaseRequest } from "../../features/store/types/dgcoin-purchase-types";
import { ElectricityPurchaseRequest } from "../../features/store/types/electricity-types";
import useClickOutside from "../../hooks/useClickOutside";
import {
  convertCurrencyAmount,
  formatCurrency,
  isInReactNativeWebView,
} from "../../utils/utils";
import BankTransferDetails from "./BankTransferDetails";
import { isSwitchedOff, OPAY_CHECKOUT_SETTING } from "../../utils/settings";
import {
  resetPurchaseState as resetTransferState,
  setPaymentDetails as setTransferPaymentDetails,
  selectTransferPayment,
} from "../../features/store/transferPaymentSlice";
import { checkoutCart } from "../../features/store/cartSlice";

const PaymentModal = (props: TransactionData) => {
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null,
  );

  const [quantity, setQuantity] = useState<number>(props.quantity);

  const handleIncrement = () => setQuantity((prev) => prev + 1);
  const handleDecrement = () =>
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  const dispatch = useDispatch<AppDispatch>();
  const airtimeLoading = useAppSelector(selectAirtimeLoading);
  const airtimePaymentUrl = useAppSelector(selectAirtimePaymentUrl);
  const airtimeError = useAppSelector(selectAirtimeError);

  const dataLoading = useAppSelector(selectDataLoading);
  const dataPaymentUrl = useAppSelector(selectDataPaymentUrl);
  const dataError = useAppSelector(selectDataError);

  const electricityLoading = useAppSelector(selectElectricLoading);
  const electricityPaymentUrl = useAppSelector(selectElectricPaymentUrl);
  const electricityError = useAppSelector(selectElectricError);
  const cableLoading = useAppSelector(selectCableLoading);
  const cablePaymentUrl = useAppSelector(selectCablePaymentUrl);
  const cableError = useAppSelector(selectCableError);
  const giftcardLoading = useAppSelector(selectGiftcardLoading);
  const giftcardPaymentUrl = useAppSelector(selectGiftcardPaymentUrl);
  const giftcardError = useAppSelector(selectGiftcardError);
  const topupLoading = useAppSelector(selectTopupLoading);
  const topupPaymentUrl = useAppSelector(selectTopupPaymentUrl);
  const topupError = useAppSelector(selectTopupError);
  const gamecardLoading = useAppSelector(selectGamecardLoading);
  const gamecardPaymentUrl = useAppSelector(selectGamecardPaymentUrl);
  const gamecardError = useAppSelector(selectGamecardError);
  const dgcoinLoading = useAppSelector(selectDGCoinLoading);
  const dgcoinPaymentUrl = useAppSelector(selectDGCoinPaymentUrl);
  const dgcoinError = useAppSelector(selectDGCoinError);
  const transferPayment = useAppSelector(selectTransferPayment);

  const navigate = useNavigate();
  const handleCloseModal = () => {
    if (successMessage) {
      navigate("/");
      return;
    }
    props.togglePaymentModal();
  };
  const clickref = useClickOutside(handleCloseModal);

  const { user } = useAppSelector((state) => state.auth);
  const { data: appData } = useAppSelector((state) => state.appData);
  const { selectedCurrency, userLocation } = useAppSelector(
    (state) => state.locale,
  );

  const stateMap = {
    airtime: {
      loading: airtimeLoading,
      url: airtimePaymentUrl,
      error: airtimeError,
    },
    data: { loading: dataLoading, url: dataPaymentUrl, error: dataError },
    electric: {
      loading: electricityLoading,
      url: electricityPaymentUrl,
      error: electricityError,
    },
    cable: { loading: cableLoading, url: cablePaymentUrl, error: cableError },
    gift_cards: {
      loading: giftcardLoading,
      url: giftcardPaymentUrl,
      error: giftcardError,
    },
    topup: { loading: topupLoading, url: topupPaymentUrl, error: topupError },
    game_cards: {
      loading: gamecardLoading,
      url: gamecardPaymentUrl,
      error: gamecardError,
    },
    dg_coins: {
      loading: dgcoinLoading,
      url: dgcoinPaymentUrl,
      error: dgcoinError,
    },
  };

  const currentProduct = stateMap[props.type as keyof typeof stateMap] || {};
  const loading = currentProduct.loading ?? false;
  const paymentUrl = currentProduct.url ?? null;
  const error = currentProduct.error ?? null;

  // track which payment method is currently processing locally
  const [processingMethod, setProcessingMethod] = React.useState<
    "ATM" | "TRANSFER" | "COIN" | "POINT" | "OPAY" | "NOWPAY" | "BLUSALT" | null
  >(null);

  // Only act on a payment URL the user just initiated in this session. The
  // modal stays mounted while closed, so a stale paymentUrl left in Redux from
  // a previous attempt must not auto-trigger a redirect to an expired checkout.
  const paymentInitiatedRef = React.useRef(false);

  // Redirect to the hosted checkout (OPay/ATM/NOWPayments) when a payment URL
  // becomes available. This is the proven flow on both web and the mobile WebView.
  React.useEffect(() => {
    if (!paymentUrl || !paymentInitiatedRef.current) return;
    paymentInitiatedRef.current = false;
    window.location.href = paymentUrl;
  }, [paymentUrl]);

  React.useEffect(() => {
    if (props.quantity !== undefined) {
      setQuantity(props.quantity);
    }
  }, [props.quantity]);

  // clear local processing flag when global loading stops
  React.useEffect(() => {
    if (!loading) {
      setProcessingMethod(null);
    }
  }, [loading]);

  React.useEffect(() => {
    if (!props.isOpen) {
      dispatch(resetTransferState());
      setSuccessMessage(null);
      setProcessingMethod(null);
      paymentInitiatedRef.current = false;
    }
  }, [props.isOpen, dispatch]);

  const handleTransferProcessingFallback = React.useCallback(() => {
    dispatch(resetTransferState());
    setSuccessMessage("Transaction is processing");
    setProcessingMethod(null);
  }, [dispatch]);

  const handleTransferSuccess = React.useCallback(() => {
    dispatch(resetTransferState());
    setSuccessMessage("Payment successful!");
    setProcessingMethod(null);
  }, [dispatch]);

  if (!props.isOpen) return null;

  const currencies = appData.currencies || [];
  const getCurrencyById = (currencyId?: number) =>
    currencies.find((currency: any) => currency.id === currencyId);
  const dgCoinCurrency =
    currencies.find((currency: any) => currency.symbol === "DG") ||
    currencies.find((currency: any) =>
      String(currency.name || "")
        .toLowerCase()
        .includes("dg coin"),
    );

  const formatConvertedPrice = (
    amount: number | string,
    currencyId?: number,
  ) => {
    const fromCurrency =
      getCurrencyById(currencyId) || selectedCurrency || currencies[0];
    const toCurrency = selectedCurrency || fromCurrency;
    const numericAmount = Number(amount);
    const converted = convertCurrencyAmount(
      numericAmount,
      fromCurrency,
      toCurrency,
    );
    return {
      symbol: toCurrency?.symbol || "",
      value: formatCurrency(converted),
    };
  };

  const getDGCoinValue = (amount: number | string, currencyId?: number) => {
    const fromCurrency = getCurrencyById(currencyId) || dgCoinCurrency;
    if (!fromCurrency || !dgCoinCurrency) {
      return Number(amount) || 0;
    }
    const numericAmount = Number(amount);
    return convertCurrencyAmount(numericAmount, fromCurrency, dgCoinCurrency);
  };

  const isFixed = props.selectedPlan?.price_type === "fixed";

  const singleWalletPrice = isFixed
    ? Number(props.selectedPlan?.wallet_price ?? 0)
    : Number(
        props.selectedPlan
          ? (props.selectedPlan?.wallet_price *
              Number(props.additionalInputValue || 0)) /
              100
          : 0,
      );

  const totalWalletPrice = singleWalletPrice * quantity;

  const planCurrencyId = props.selectedPlan?.price_currency_id;
  const discountRaw = Number(props.selectedPlan?.discount ?? 0);
  const walletPriceRaw = Number(props.selectedPlan?.wallet_price ?? 0);
  const hasDiscount = discountRaw > 0 && discountRaw > walletPriceRaw;

  const singleOriginalWalletPrice = hasDiscount
    ? isFixed
      ? discountRaw
      : (discountRaw * Number(props.additionalInputValue || 0)) / 100
    : null;

  const totalOriginalWalletPrice =
    singleOriginalWalletPrice !== null
      ? singleOriginalWalletPrice * quantity
      : null;

  const totalDiscountAmount =
    totalOriginalWalletPrice !== null
      ? totalOriginalWalletPrice - totalWalletPrice
      : null;

  const discountPercent = hasDiscount
    ? ((discountRaw - walletPriceRaw) / discountRaw) * 100
    : 0;

  const convertedWallet = formatConvertedPrice(
    totalWalletPrice,
    planCurrencyId,
  );

  const convertedSingleWallet = formatConvertedPrice(
    singleWalletPrice,
    planCurrencyId,
  );

  const convertedOriginal =
    totalOriginalWalletPrice !== null
      ? formatConvertedPrice(totalOriginalWalletPrice, planCurrencyId)
      : null;
  const convertedDiscount =
    totalDiscountAmount !== null
      ? formatConvertedPrice(totalDiscountAmount, planCurrencyId)
      : null;

  const singleGgCoinValue = getDGCoinValue(singleWalletPrice, planCurrencyId);
  const dgCoinValue = getDGCoinValue(totalWalletPrice, planCurrencyId);
  const formattedDgCoinValue = formatCurrency(dgCoinValue);
  const formattedSingleDgCoinValue = formatCurrency(singleGgCoinValue);

  const showBankTransfer =
    selectedCurrency?.symbol === "₦" ||
    selectedCurrency?.name?.toLowerCase().includes("naira");

  // Payment-method availability. Mirrors the backend PaymentMethodGate:
  // Flutterwave card is retired for Nigerian users, who get Blusalt (cards,
  // transfer, USSD, QR) instead. "Nigerian" comes from the country the backend
  // resolved for this visitor's IP (/user-country); if that failed, paying in
  // Naira is the fallback signal. OPay is switched on/off by admin from the
  // Settings page ("opay_checkout").
  const OPAY_ENABLED = !isSwitchedOff(appData?.settings, OPAY_CHECKOUT_SETTING);
  // Blusalt is on trial: checkout works, but its payment-confirmation webhook
  // is still unproven, so an order can be paid and stay pending. Set to false
  // to withdraw it again (and restore 'BLUSALT' to PaymentMethodGate::RETIRED).
  const BLUSALT_ENABLED = true;
  const detectedCountry = userLocation.data?.countryCode?.toUpperCase();
  const isNigerianUser = detectedCountry
    ? detectedCountry === "NG"
    : showBankTransfer;
  // Blusalt, bank transfer and OPay are Nigerian rails: offered only to
  // Nigerian users (whatever currency a visitor abroad has selected), as
  // PaymentMethodGate enforces. Everyone else gets card (Flutterwave) and crypto.
  const showBlusalt = BLUSALT_ENABLED && isNigerianUser;
  const showTransfer = showBankTransfer && isNigerianUser;
  const showAtmCard = !isNigerianUser;
  const showOpay = OPAY_ENABLED && showTransfer;

  const getBasePriceByMethod = () => {
    const plan = props.selectedPlan;

    switch (processingMethod) {
      case "ATM":
      case "TRANSFER":
      case "OPAY":
      case "NOWPAY":
      case "BLUSALT":
        return Number(plan?.atm_price ?? plan?.wallet_price ?? 0);
      case "COIN":
      case "POINT":
      default:
        return Number(plan?.wallet_price ?? 0);
    }
  };

  const activePrice =
    props.selectedPlan?.price_type !== "percent"
      ? getBasePriceByMethod()
      : (getBasePriceByMethod() * Number(props.additionalInputValue || 0)) /
        100;

  const shouldEarnBonus = processingMethod !== "POINT";

  const singleBonusAmount = shouldEarnBonus
    ? props.selectedPlan?.price_type === "percent"
      ? (Number(props.selectedPlan?.point_bonus ?? 0) / 100) * activePrice
      : Number(props.selectedPlan?.point_bonus ?? 0)
    : 0;

  const totalBonusAmount = singleBonusAmount * quantity;

  const convertedBonus = formatConvertedPrice(totalBonusAmount, planCurrencyId);

  // Generic payment handler for ATM, Bank Transfer and Coin (points)

  const handlePayment = async (
    method:
      "ATM" | "TRANSFER" | "COIN" | "POINT" | "OPAY" | "NOWPAY" | "BLUSALT",
  ) => {
    if (loading) {
      return;
    }
    // reset any previous purchase state and mark this method as processing
    switch (props.type) {
      case "airtime":
        dispatch(resetAirtimeState());
        break;
      case "data":
        dispatch(resetDataState());
        break;
      case "electric":
        dispatch(resetElectricState());
        break;
      case "cable":
        dispatch(resetCableState());
        break;
      case "gift_cards":
        dispatch(resetGiftcardState());
        break;
      case "topup":
        dispatch(resetTopupState());
        break;
      case "game_cards":
        dispatch(resetGamecardState());
        break;
      case "dg_coins":
        dispatch(resetDGCoinState());
        break;
      default:
        toast.error("Unsupported product type.");
        return;
    }
    setProcessingMethod(method);
    setSuccessMessage(null);
    // ATM/OPAY/NOWPAY produce a hosted-checkout URL we must open; arm the
    // redirect effect so it only fires for this user-initiated attempt (and
    // never from a stale paymentUrl when the modal is merely reopened).
    if (
      method === "ATM" ||
      method === "OPAY" ||
      method === "NOWPAY" ||
      method === "BLUSALT"
    ) {
      paymentInitiatedRef.current = true;
    }
    if (method === "TRANSFER") {
      dispatch(resetTransferState());
    }

    let action;
    let res: any;

    try {
      if (quantity > 1) {
        const extra = props.additionalInputValue?.trim() || undefined;
        const isAmount =
          props.type === "airtime" ||
          props.type === "electric" ||
          (props.selectedPlan.price_type === "percent" &&
            props.type !== "topup");

        const items = [
          {
            type: props.type,
            plan_id: Number(props.selectedPlan.id),
            quantity,
            unique_number:
              props.type === "dg_coins" ? undefined : props.unique_number,
            amount: isAmount && extra ? Number(extra) : undefined,
            server: props.type === "topup" ? extra : undefined,
          },
        ];
        const payload = {
          userId: props.userId,
          payment_method: method,
          currency_id: selectedCurrency?.id,
          source: "WEB",
          items,
        } as const;
        res = await dispatch(checkoutCart(payload)).unwrap();
      } else {
        let payload:
          | AirtimePurchaseRequest
          | DataPurchaseRequest
          | ElectricityPurchaseRequest
          | CablePurchaseRequest
          | GiftcardPurchaseRequest
          | TopupPurchaseRequest
          | GamecardPurchaseRequest
          | DGCoinPurchaseRequest;

        switch (props.type) {
          case "airtime":
            payload = {
              unique_number: props.unique_number || "",
              plan_id: props.selectedPlan.id,
              payment_method: method,
              amount: Number(props.additionalInputValue),
              source: "WEB",
              currency_id: selectedCurrency?.id,
            } as AirtimePurchaseRequest;
            break;
          case "data":
            payload = {
              unique_number: props.unique_number || "",
              plan_id: props.selectedPlan.id,
              payment_method: method,
              source: "WEB",
              currency_id: selectedCurrency?.id,
            } as DataPurchaseRequest;
            break;
          case "electric":
            payload = {
              unique_number: props.unique_number || "",
              plan_id: props.selectedPlan.id,
              payment_method: method,
              amount: Number(props.additionalInputValue),
              source: "WEB",
              currency_id: selectedCurrency?.id,
            } as ElectricityPurchaseRequest;
            break;
          case "cable":
            payload = {
              unique_number: props.unique_number || "",
              plan_id: props.selectedPlan.id,
              payment_method: method,
              source: "WEB",
              currency_id: selectedCurrency?.id,
            } as CablePurchaseRequest;
            break;
          case "gift_cards":
            payload = {
              unique_number: props.unique_number || "",
              plan_id: props.selectedPlan.id,
              payment_method: method,
              source: "WEB",
              currency_id: selectedCurrency?.id,
            } as GiftcardPurchaseRequest;
            break;
          case "topup":
            payload = {
              unique_number: props.unique_number || "",
              plan_id: props.selectedPlan.id,
              payment_method: method,
              source: "WEB",
              currency_id: selectedCurrency?.id,
              // Server/zone value for games that require it (e.g. Blood Strike, Mobile Legends)
              server: props.additionalInputValue || undefined,
            } as TopupPurchaseRequest;
            break;
          case "game_cards":
            payload = {
              unique_number: props.unique_number || "",
              plan_id: props.selectedPlan.id,
              payment_method: method,
              source: "WEB",
              currency_id: selectedCurrency?.id,
            } as GamecardPurchaseRequest;
            break;
          case "dg_coins":
            payload = {
              plan_id: props.selectedPlan.id,
              payment_method: method,
              source: "WEB",
              currency_id: selectedCurrency?.id,
            } as DGCoinPurchaseRequest;
            break;
          default:
            toast.error("Unsupported product type.");
            return;
        }

        switch (props.type) {
          case "airtime":
            action = purchaseAirtime(payload as AirtimePurchaseRequest);
            break;
          case "data":
            action = purchaseData(payload as DataPurchaseRequest);
            break;
          case "electric":
            action = purchaseElectricity(payload as ElectricityPurchaseRequest);
            break;
          case "cable":
            action = purchaseCable(payload as CablePurchaseRequest);
            break;
          case "gift_cards":
            action = purchaseGiftcard(payload as GiftcardPurchaseRequest);
            break;
          case "topup":
            action = purchaseTopup(payload as TopupPurchaseRequest);
            break;
          case "game_cards":
            action = purchaseGamecard(payload as GamecardPurchaseRequest);
            break;
          case "dg_coins":
            action = purchaseDGCoin(payload as DGCoinPurchaseRequest);
            break;
          default:
            toast.error("Unsupported product type.");
            return;
        }
        res = await dispatch(action).unwrap();
      }

      const responseData = res?.data || res;

      if (responseData?.payment_url) {
        if (props.isInCart) {
          props.removeFromCart();
        }
        window.location.href = responseData.payment_url;
        return;
      }

      // Bank Transfer Account Details
      if (method === "TRANSFER" && responseData?.account_number) {
        dispatch(
          setTransferPaymentDetails({
            bank: responseData.bank_name,
            bank_name: responseData.bank_name,
            account_name: responseData.account_name,
            account_number: responseData.account_number,
            reference: responseData.reference,
            accountExpiry: responseData.accountExpiry,
            amount: responseData.amount,
          }),
        );
        setProcessingMethod(null);
        return;
      }

      setSuccessMessage(res?.message || "Payment successful!");

      if (props.isInCart) {
        props.removeFromCart();
      }

      const hasAlreadyReviewed = localStorage.getItem(
        "has_triggered_app_review",
      );

      if (isInReactNativeWebView() && !hasAlreadyReviewed) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ action: "request-app-review" }),
        );
        localStorage.setItem("has_triggered_app_review", "true");
      }
    } catch (err: any) {
      // unwrap throws the rejected action payload or error
      const msg =
        err?.message ||
        err?.payload?.message ||
        err?.data?.message ||
        err?.error ||
        "Payment failed";

      toast.error(msg);
      setProcessingMethod(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={clickref}
        className="bg-white dark:bg-black rounded-lg p-6 w-[400px] bp:w-full max-w-md mx-[10px] h-[600px] bp:h-[684px] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xs bp:text-base font-semibold text-black dark:text-white">
            Checkout
          </h2>
          <button
            onClick={handleCloseModal}
            aria-label="Close modal"
            className="text-[#475467] dark:text-white bg-[#F4F4F5] dark:bg-[#FFFFFF1A] rounded-2xl h-6 w-5 hover:text-gray-700  dark:hover:text-gray-200"
          >
            &times;
          </button>
        </div>

        {Number(props.selectedPlan?.point_bonus ?? 0) > 0 && (
          <div className="bg-[#FFF9E7] h-10 dark:bg-[#DC6803] p-3 rounded-3xl mb-6 flex items-center justify-center">
            <img src="/png/dg.png" alt="Coins" className="w-[24px] h-[24px]" />
            <p className="text-[10px] bp:text-sm text-[#DC6803] dark:text-white">
              Earn {convertedBonus.symbol}
              {convertedBonus.value} in DG points bonus from this purchase
            </p>
          </div>
        )}

        <div className="flex items-center gap-3 mb-2 bp:mb-6">
          <div className="flex-shrink-0">
            <img
              src={
                props.selectedPlan?.image_url ||
                props.selectedPlan?.category?.provider?.image_url ||
                props.selectedPlan?.provider?.image_url ||
                ""
              }
              alt="product_img"
              className="w-20 bp:w-[100px] aspect-square rounded-lg object-cover"
            />
          </div>
          <div className="border border-border-border1 dark:border-tabs p-2 rounded-lg flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-xs bp:text-base text-bold text-black dark:text-white truncate">
                {props.selectedPlan?.name}
              </h3>
              {props.selectedPlan.discount > 0 && (
                <span className="bg-[#FEF3F2] rounded-xl shrink-0">
                  <p className="text-[#D92D20] text-[10px] font-semibold font-satoshi px-[2px] py-[2px] whitespace-nowrap">
                    {Math.round(discountPercent)}% off
                  </p>
                </span>
              )}
            </div>
            <p className="font-lt text-[10px] bp:text-sm  text-gray-500 dark:text-gray-400">
              {props.selectedPlan?.category?.name}
            </p>
            <div className="flex items-center mt-1 gap-2">
              {props.selectedPlan.price_type === "fixed" && (
                <>
                  <div className="bg-[#039EF2] text-white px-1 rounded-2xl flex items-center gap-1 shrink-0">
                    <img
                      src="/png/dg.png"
                      alt="Coins"
                      className="w-[20px] h-[20px]"
                    />
                    <span className="text-[10px] bp:text-xs font-semibold whitespace-nowrap">
                      {formattedSingleDgCoinValue}
                    </span>
                  </div>
                  <span className="text-xs bp:text-[10px] text-black dark:text-white">
                    {convertedSingleWallet.symbol}
                    {convertedSingleWallet.value}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 py-4">
          {props.selectedPlan.price_type === "percent" && (
            <div className="flex justify-between mb-1 bp:mb-2 font-inter">
              <span className="text-[#667085] dark:text-white text-[10px] bp:text-sm">
                Amount Requested
              </span>
              <span className="text-black text-[10px] bp:text-sm dark:text-white ">
                {`${convertedWallet.symbol}${Number(props.additionalInputValue) * quantity}`}
              </span>
            </div>
          )}

          <div className="flex justify-between mb-1 bp:mb-2 font-inter">
            <span className="text-[#667085] dark:text-white text-[10px] bp:text-sm">
              Subtotal
            </span>
            <span className="text-black text-[10px] bp:text-sm dark:text-white ">
              {hasDiscount && convertedOriginal
                ? `${convertedOriginal.symbol}${convertedOriginal.value}`
                : `${convertedWallet.symbol}${convertedWallet.value}`}
            </span>
          </div>
          {hasDiscount && convertedDiscount && (
            <div className="flex justify-between mb-1 bp:mb-2 font-inter">
              <span className="text-[#667085] text-[10px] bp:text-sm dark:text-white">
                Discount
              </span>
              <span className="text-black dark:text-white text-[10px] bp:text-sm">
                {convertedDiscount.symbol}
                {convertedDiscount.value}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
          <span className="text-[10px] bp:text-xs text-gray-500 dark:text-gray-400">
            Quantity:
          </span>
          <div className="flex items-center border border-gray-300 dark:border-gray-700 rounded-lg px-2 py-0.5 gap-3">
            <button
              type="button"
              onClick={handleDecrement}
              className="text-black dark:text-white font-bold text-xs hover:opacity-70 disabled:opacity-30"
              disabled={quantity <= 1}
            >
              -
            </button>
            <span className="text-xs font-semibold text-black dark:text-white">
              {quantity}
            </span>
            <button
              type="button"
              onClick={handleIncrement}
              className="text-black dark:text-white font-bold text-xs hover:opacity-70"
            >
              +
            </button>
          </div>
        </div>

        <div className="border-t border-dashed border-gray-200 dark:border-white py-4 font-inter">
          <div className="flex justify-between items-center">
            <span className="text-[#101828] text-[10px] bp:text-sm dark:text-gray-400">
              Total
            </span>
            <div className="flex items-center">
              <div className="bg-[#039EF2] text-white  px-2 py-1 rounded-2xl gap-1 flex items-center mr-2">
                <div className="w-4 h-4  flex items-center">
                  <img
                    src="/png/dg.png"
                    alt="Coins"
                    className="w-[20px] h-[20px]"
                  />
                </div>
                <span className=" text-[10px] bp:text-xs font-inter font-semibold">
                  {formattedDgCoinValue}
                </span>
              </div>
              <span className="text-[10px] bp:text-sm font-bold text-black dark:text-white">
                {convertedWallet.symbol}
                {convertedWallet.value}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-2 bp:mt-6 bg-[#EAECF0] dark:bg-[#FFFFFF1A] rounded-lg p-4">
          {transferPayment.paymentDetails ? (
            <BankTransferDetails
              onProcessingFallback={handleTransferProcessingFallback}
              onSuccess={handleTransferSuccess}
            />
          ) : successMessage ? (
            <div className="bg-white dark:bg-[#FFFFFF1A] rounded-lg p-4 text-center">
              <h3 className="text-sm bp:text-base font-semibold text-black dark:text-white">
                {successMessage.toLowerCase().includes("processing")
                  ? "Transaction Processing"
                  : "Payment Successful"}
              </h3>
              <p className="mt-2 text-xs bp:text-sm text-gray-600 dark:text-gray-300">
                {successMessage}
              </p>
              <Link
                to="/order-history"
                className="inline-flex items-center justify-center mt-4 w-full border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-3xl text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
              >
                View Orders
              </Link>
            </div>
          ) : (
            <>
              {/* Coin payment button */}
              {user &&
                props.type !== "dg_coins" &&
                user.dg_coin_wallet_balance >= dgCoinValue && (
                  // compare to dgcoinvalue since they are of same exchange rate
                  <>
                    <button
                      className={`relative h-[50px] bp:h-[60px] w-full max-w-md flex items-center justify-between rounded-lg p-4 overflow-hidden ${
                        loading && processingMethod !== "COIN"
                          ? "blur-sm opacity-60 cursor-not-allowed"
                          : ""
                      }`}
                      style={{
                        backgroundImage: `url(/images/bg.png)`,
                        backgroundPosition: "center bottom",
                        backgroundSize: "cover",
                      }}
                      onClick={() => handlePayment("COIN")}
                      disabled={loading}
                    >
                      {/* Left side with coin icon */}
                      <div className="flex items-center gap-3 z-10">
                        <div className="flex items-center justify-center">
                          <div className="w-6 h-6 mr-1 flex items-center gap-1">
                            <img
                              src="/png/dg.png"
                              alt="Coins"
                              className="w-[40px] h-[40px]"
                            />
                            <div className="flex flex-col mt-2">
                              <p className="text-[8px] font-medium text-blue-100">
                                Pay with available coin
                              </p>
                              <p className="text-xl font-lt font-bold text-white">
                                {processingMethod === "COIN" && loading
                                  ? "Processing..."
                                  : formatCurrency(
                                      Number(user.dg_coin_wallet_balance),
                                    )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right side with arrow icon */}
                      <div className="bg-white  w-5 h-5 bp:w-6 bp:h-6 flex items-center rounded-full p-2 z-10">
                        <ForwardIcon />
                      </div>
                    </button>

                    <div
                      className={`flex items-center my-4 ${
                        loading && processingMethod !== "COIN"
                          ? "blur-sm opacity-60"
                          : ""
                      }`}
                    >
                      {/* <div className="flex-grow h-px bg-gray-200"></div>
                      <span className="px-3 text-gray-500 text-sm uppercase">
                        OR
                      </span>
                      <div className="flex-grow h-px bg-gray-200"></div> */}
                    </div>
                  </>
                )}

              {/* Point payment button */}
              {user &&
                props.type !== "dg_coins" &&
                user.dg_point_wallet_balance >= dgCoinValue && (
                  // compare to dgcoinvalue since they are of same exchange rate
                  <>
                    <button
                      className={`relative h-[50px] bp:h-[60px] w-full max-w-md flex items-center justify-between rounded-lg p-4 overflow-hidden bg-[#FFF4EA] ${
                        loading && processingMethod !== "POINT"
                          ? "blur-sm opacity-60 cursor-not-allowed"
                          : ""
                      }`}
                      onClick={() => handlePayment("POINT")}
                      disabled={loading}
                    >
                      {/* Left side with coin icon */}
                      <div className="flex items-center gap-3 z-10">
                        <div className="flex items-center justify-center">
                          <div className="w-6 h-6 mr-1 flex items-center gap-1">
                            <img
                              src="/images/diamond.png"
                              alt="Coins"
                              className="w-[40px] h-[40px]"
                            />
                            <div className="flex flex-col mt-2">
                              <p className="text-[8px] font-medium text-[#CE8B41] whitespace-nowrap">
                                Pay with available point
                              </p>
                              <p className="text-xl font-lt font-bold text-[#CE8B41]">
                                {processingMethod === "POINT" && loading
                                  ? "Processing..."
                                  : formatCurrency(
                                      Number(user.dg_point_wallet_balance),
                                    )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right side with arrow icon */}
                      <div className="bg-white  w-5 h-5 bp:w-6 bp:h-6 flex items-center rounded-full p-2 z-10">
                        <ForwardIcon />
                      </div>
                    </button>
                    <div
                      className={`flex items-center my-4 ${
                        loading && processingMethod !== "POINT"
                          ? "blur-sm opacity-60"
                          : ""
                      }`}
                    >
                      {/* <div className="flex-grow h-px bg-gray-200"></div>
                      <span className="px-3 text-gray-500 text-sm uppercase">
                        OR
                      </span>
                      <div className="flex-grow h-px bg-gray-200"></div> */}
                    </div>
                  </>
                )}

              {/* Bank Transfer Payment Button */}

              {showTransfer && (
                <>
                  <button
                    className={`w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white ${
                      loading && processingMethod !== "TRANSFER"
                        ? "blur-sm opacity-60 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={() => handlePayment("TRANSFER")}
                    disabled={loading}
                  >
                    <div className="flex space-x-2 mr-2"></div>
                    <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                      {processingMethod === "TRANSFER" && loading
                        ? "Processing..."
                        : "Pay with Bank Transfer"}
                    </span>
                  </button>

                  <div
                    className={`flex items-center my-4 ${
                      loading && processingMethod !== "TRANSFER"
                        ? "blur-sm opacity-60"
                        : ""
                    }`}
                  >
                    {/* <div className="flex-grow h-px bg-gray-200"></div>
                    <span className="px-3 text-gray-500 text-sm uppercase">
                      OR
                    </span>
                    <div className="flex-grow h-px bg-gray-200"></div> */}
                  </div>
                </>
              )}

              {/* Blusalt Payment Button (Nigerian users): cards, mobile money, OPay, PalmPay */}
              {showBlusalt && (
                <>
                  <button
                    className={`w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white ${
                      loading && processingMethod !== "BLUSALT"
                        ? "blur-sm opacity-60 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={() => handlePayment("BLUSALT")}
                    disabled={loading}
                  >
                    <span className="w-6 h-6 mr-2 flex items-center justify-center">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6"
                        aria-hidden="true"
                      >
                        <rect
                          x="2"
                          y="5"
                          width="20"
                          height="14"
                          rx="3"
                          fill="#0A5BD3"
                        />
                        <rect
                          x="2"
                          y="9"
                          width="20"
                          height="3"
                          fill="#0B3C8C"
                        />
                        <rect
                          x="5"
                          y="14"
                          width="6"
                          height="2"
                          rx="1"
                          fill="#FFFFFF"
                        />
                      </svg>
                    </span>
                    <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                      {processingMethod === "BLUSALT" && loading
                        ? "Processing..."
                        : "Pay with Card/ Momo/ Opay/ Palmpay"}
                    </span>
                  </button>

                  <div
                    className={`flex items-center my-4 ${
                      loading && processingMethod !== "BLUSALT"
                        ? "blur-sm opacity-60"
                        : ""
                    }`}
                  />
                </>
              )}

              {/* OPay Payment Button (NGN only) - admin switch, see OPAY_ENABLED */}
              {showOpay && (
                <>
                  <button
                    className={`w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white ${
                      loading && processingMethod !== "OPAY"
                        ? "blur-sm opacity-60 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={() => handlePayment("OPAY")}
                    disabled={loading}
                  >
                    <span className="w-6 h-6 mr-2 flex items-center justify-center">
                      <svg
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-6 h-6"
                      >
                        <circle
                          cx="24"
                          cy="24"
                          r="16"
                          stroke="#52BE95"
                          strokeWidth="8"
                          fill="none"
                        />
                        <rect
                          x="2"
                          y="20"
                          width="18"
                          height="8"
                          rx="2"
                          fill="#1B1B4D"
                        />
                      </svg>
                    </span>
                    <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                      {processingMethod === "OPAY" && loading
                        ? "Processing..."
                        : "Pay with OPay"}
                    </span>
                  </button>

                  <div
                    className={`flex items-center my-4 ${
                      loading && processingMethod !== "OPAY"
                        ? "blur-sm opacity-60"
                        : ""
                    }`}
                  >
                    {/* <div className="flex-grow h-px bg-gray-200"></div>
                    <span className="px-3 text-gray-500 text-sm uppercase">
                      OR
                    </span>
                    <div className="flex-grow h-px bg-gray-200"></div> */}
                  </div>
                </>
              )}

              {/* Crypto Payment Button (NOWPayments) - available in every region */}
              <button
                className={`w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white ${
                  loading && processingMethod !== "NOWPAY"
                    ? "blur-sm opacity-60 cursor-not-allowed"
                    : ""
                }`}
                onClick={() => handlePayment("NOWPAY")}
                disabled={loading}
              >
                <span className="w-6 h-6 mr-2 flex items-center justify-center">
                  <svg
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                  >
                    <path
                      d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.546zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.975.225.955.238c.535.136.63.486.615.766l-1.477 5.92c-.075.166-.24.406-.614.314.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.242-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.52 2.75 2.084z"
                      fill="#F7931A"
                    />
                  </svg>
                </span>
                <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                  {processingMethod === "NOWPAY" && loading
                    ? "Processing..."
                    : "Pay with Crypto"}
                </span>
              </button>

              <div
                className={`flex items-center my-4 ${
                  loading && processingMethod !== "NOWPAY"
                    ? "blur-sm opacity-60"
                    : ""
                }`}
              >
                {/* <div className="flex-grow h-px bg-gray-200"></div>
                <span className="px-3 text-gray-500 text-sm uppercase">
                  OR
                </span>
                <div className="flex-grow h-px bg-gray-200"></div> */}
              </div>

              {/* ATM Payment Button - retired for Nigerian users (Blusalt covers cards) */}
              {showAtmCard && (
                <button
                  className={`w-full bg-white dark:bg-[#FFFFFF1A] border border-gray-300 dark:border-gray-700 rounded-lg p-3 flex items-center justify-center text-black dark:text-white ${
                    loading && processingMethod !== "ATM"
                      ? "blur-sm opacity-60 cursor-not-allowed"
                      : ""
                  }`}
                  onClick={() => handlePayment("ATM")}
                  disabled={loading}
                >
                  <div className="flex space-x-2 mr-2">
                    <span className="w-8">
                      <Visa />
                    </span>
                    <span className="w-8">
                      <MasterCard />
                    </span>
                  </div>
                  <span className="text-[10px] dark:text-white bp:text-base font-semibold">
                    {processingMethod === "ATM" && loading
                      ? "Processing..."
                      : "Pay with ATM Card"}
                  </span>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
